import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  Loader,
  Paper,
  PasswordInput,
  Space,
  Text,
  TextInput,
  UnstyledButton,
  createStyles
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import bgImage from 'assets/images/bg.png';
import { AxiosError, HttpStatusCode } from 'axios';
import { AuthConnectionHeader } from 'components/AuthConnectionHeader';
import { Branding } from 'components/Branding';
import { GoogleAuthButton } from 'components/GoogleAuthButton';
import { LanguageChange } from 'components/LanguageChange';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { ForgetPasswordModal } from 'pages/Login/ForgetPasswordModal';
import { ResetTemporaryPasswordModal } from 'pages/Login/ResetTemporaryPasswordModal';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from 'services/auth-service';
import { NotificationService } from 'services/notification-service';
import { ShopifyService } from 'services/shopify-service';
import { UserService } from 'services/user-service';
import { USER_INFO_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { emailValidator, requiredInputValidator } from 'shared/utils/form-validators';
import { navigatePostLogin, navigateToLandingPage } from 'shared/utils/navigation';

const useStyles = createStyles(() => ({
  languageChangeBox: {
    position: 'absolute',
    left: 20,
    bottom: 20,
    [`@media (max-height: 880px)`]: {
      position: 'relative',
      left: 0,
      bottom: 0,
      marginTop: 20,
      marginBottom: 20
    }
  }
}));

export function LoginPage() {
  const [triggerGoogleAuth, setTriggerGoogleAuth] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { google } = queryString.parse(location.search);
  const { classes } = useStyles();

  useEffect(() => {
    if (typeof google === 'string' && google.trim() !== '' && google === 'true') setTriggerGoogleAuth(true);
  }, []);

  const loginForm = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => requiredInputValidator(value) || emailValidator(value),
      password: (value) => requiredInputValidator(value)
    }
  });

  const [resetTemporaryPasswordModalOpened, setResetTemporaryPasswordModalOpened] = useState(false);
  const [forgetPasswordModalOpened, setForgetPasswordModalOpened] = useState(false);

  const userInfoQuery = useQuery({
    queryKey: [USER_INFO_QUERY_KEY],
    queryFn: () => UserService.userInfo(),
    onSuccess: () => navigatePostLogin(queryString.parse(location.search)),
    onError: () => {
      UserService.logout();
      NotificationService.showResult(false, 'AUTH.ERROR.SIGN_IN');
    },
    enabled: false
  });

  const loginMutation = useMutation({
    mutationFn: AuthService.login,
    onSuccess: () => userInfoQuery.refetch(),
    onError: (error: AxiosError) => {
      const errorCode = error.response?.status;
      if (errorCode === HttpStatusCode.Forbidden) {
        setResetTemporaryPasswordModalOpened(true);
      } else if (errorCode === HttpStatusCode.Unauthorized) {
        NotificationService.showResult(false, 'AUTH.ERROR.WRONG_CREDENTIALS');
      } else if (errorCode === HttpStatusCode.PreconditionFailed) {
        NotificationService.showResult(false, 'AUTH.ERROR.EMAIL_NOT_VERIFIED');
      } else if (errorCode === HttpStatusCode.UnprocessableEntity) {
        NotificationService.showResult(false, 'AUTH.ERROR.CONFLICT_EMAIL_USED_BY_PROVIDER');
      } else {
        NotificationService.showResult(false, 'AUTH.ERROR.SIGN_IN');
      }
    }
  });

  return (
    <Flex
      direction="column"
      justify="center"
      align="center"
      h="100svh"
      w="100vw"
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',

        backgroundPosition: 'center'
      }}>
      <Paper withBorder p={20} mx={5} shadow="md">
        <Center mb={10}>
          {
            // User is connecting their Shopify Store
            ShopifyService.isShopifyStoreDataInLocalStorage() ? (
              <AuthConnectionHeader
                connectionTitle={ShopifyService.getShopifyShopUrlFromLocalStorage() ?? ''}
                connectionAppIcon={<ShopifyIcon w={30} h={30} />}
                type="signIn"
              />
            ) : (
              <UnstyledButton onClick={() => navigateToLandingPage()}>
                <Branding width={110} />
              </UnstyledButton>
            )
          }
        </Center>

        <form onSubmit={loginForm.onSubmit((credentials) => loginMutation.mutate(credentials))}>
          <TextInput
            label={t('AUTH.EMAIL.LABEL')}
            placeholder={t('AUTH.EMAIL.PLACEHOLDER')}
            {...loginForm.getInputProps('email')}
          />
          <PasswordInput
            label={t('AUTH.PASSWORD.LABEL')}
            placeholder={t('AUTH.PASSWORD.PLACEHOLDER')}
            mt="md"
            {...loginForm.getInputProps('password')}
          />
          <Space h="xs" />
          <Flex direction="row" mt={10}>
            <Text>{t('AUTH.FORGET_PASSWORD.PROMPT')}</Text>
            <Space w={5} />
            <Text
              sx={() => ({
                '&:hover': {
                  cursor: 'pointer'
                },
                textDecoration: 'underline'
              })}
              onClick={() => setForgetPasswordModalOpened(true)}>
              {t('AUTH.FORGET_PASSWORD.PROMPT_ACTION_LABEL')}
            </Text>
          </Flex>
          <Space h={20} />
          <Button type="submit" fullWidth disabled={loginMutation.isLoading}>
            {loginMutation.isLoading ? <Loader size="sm" /> : t('AUTH.SIGN_IN')}
          </Button>
          <Space h={10} />
          <GoogleAuthButton disabled={loginMutation.isLoading} trigger={triggerGoogleAuth} />
          <Space h={10} />
          <Flex direction="row" mt={10}>
            <Text>{t('AUTH.NO_ACCOUNT')}</Text>
            <Space w={5} />
            <Text
              sx={() => ({
                '&:hover': {
                  cursor: 'pointer'
                },
                textDecoration: 'underline'
              })}
              // Login params should be passed to sign up page
              onClick={() => navigate(ROUTE_LINKS.signUp + (location.search ?? ''))}>
              {t('AUTH.SIGN_UP')}
            </Text>
          </Flex>
        </form>
      </Paper>

      <Box className={classes.languageChangeBox}>
        <LanguageChange />
      </Box>

      <ResetTemporaryPasswordModal
        opened={resetTemporaryPasswordModalOpened}
        email={loginForm.values.email}
        temporaryPassword={loginForm.values.password}
        onClose={() => setResetTemporaryPasswordModalOpened(false)}
      />

      <ForgetPasswordModal opened={forgetPasswordModalOpened} onClose={() => setForgetPasswordModalOpened(false)} />
    </Flex>
  );
}
